import { Component, inject, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CpaasAuthService } from '../document-processing/cpaas-auth.service';
import { CpaasClientsService } from '../document-processing/cpaas-clients.service';
import { CpaasClient } from '../document-processing/cdr-clients.constants';

const API_BASE  = '/cpaas-telekom/api/document-store';
const CATEGORIES = ['WHOLESALE_BILL', 'ENTERPRISE_BILL', 'ENTERPRISE_CDR', 'CONTRACTS', 'USE_CASES', 'OTHERS'];

@Component({
  selector: 'app-document-store',
  templateUrl: './document-store.component.html',
  styles: [`
    .token-ok    { color: #16a34a; font-weight: 600; }
    .token-warn  { color: #d97706; font-weight: 600; }
    .token-error { color: #dc2626; font-weight: 600; }
    pre { white-space: pre-wrap; word-break: break-all; font-size: 0.75rem; }
  `]
})
export class DocumentStoreComponent implements OnInit {
  private http       = inject(HttpClient);
  auth               = inject(CpaasAuthService);
  private clientsSvc = inject(CpaasClientsService);

  canAccess = true;
  loading   = false;

  // Auth form fields
  username = '';
  password = '';
  otp      = '';

  // Document list
  files      : any[] = [];
  totalFiles = 0;

  // Filters
  dateCreatedFrom      = '';
  dateCreatedTo        = '';
  category             = '';
  nameContains         = '';
  descriptionContains  = '';
  wholesaleCustomerIds : string[] = [];
  serviceNames         = '';

  clientOptions: CpaasClient[] = [];
  orderBy        = 'dateCreated';
  orderDirection = 'DESC';
  page           = 0;
  pageSize       = 20;

  readonly categories = CATEGORIES;

  // Custom endpoint tester
  customPath        = '/v2/files';
  customMethod      = 'GET';
  customQueryParams = '';

  lastResponse = '';
  lastError    = '';

  // Delegating getters — the HTML template keeps the same bindings
  get accessToken()      { return this.auth.accessToken; }
  get tokenStatusClass() { return this.auth.tokenStatusClass; }
  get tokenStatusLabel() { return this.auth.tokenStatusLabel; }

  ngOnInit() {
    this.clientsSvc.getClients().subscribe(clients => {
      this.clientOptions = clients;
    });
  }

  async login() {
    if (!this.username || !this.password) {
      this.lastError = 'Introduce usuario y contraseña.';
      return;
    }
    this.loading   = true;
    this.lastError = '';
    try {
      await this.auth.login(this.username, this.password, this.otp || undefined);
      this.lastResponse = 'Sesión iniciada correctamente.';
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }

  async refreshAccessToken() {
    this.loading   = true;
    this.lastError = '';
    try {
      await this.auth.refreshAccessToken();
      this.lastResponse = 'Token refrescado correctamente.';
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }

  clearSession() {
    this.auth.clearSession();
    this.files        = [];
    this.lastResponse = '';
    this.lastError    = '';
  }

  private async ensureValidToken(): Promise<boolean> {
    if (!this.auth.accessToken) {
      this.lastError = 'No hay sesión activa. Haz login primero.';
      return false;
    }
    const ok = await this.auth.ensureValidToken();
    if (!ok) this.lastError = 'Sesión expirada. Haz login de nuevo.';
    return ok;
  }

  async listFiles() {
    if (!await this.ensureValidToken()) return;
    this.loading   = true;
    this.lastError = '';
    this.files     = [];

    try {
      let params = new HttpParams()
        .set('page',     this.page.toString())
        .set('pageSize', this.pageSize.toString());

      if (this.dateCreatedFrom)     params = params.set('dateCreatedFrom', this.dateCreatedFrom + 'T00:00:00Z');
      if (this.dateCreatedTo)       params = params.set('dateCreatedTo',   this.dateCreatedTo   + 'T23:59:59Z');
      if (this.category)            params = params.set('category',        this.category);
      if (this.nameContains)        params = params.set('nameContains',    this.nameContains);
      if (this.descriptionContains) params = params.set('descriptionContains', this.descriptionContains);
      for (const id of this.wholesaleCustomerIds) {
        params = params.append('wholesaleCustomerIds', id);
      }
      for (const sn of this.serviceNames.split(',').map(s => s.trim()).filter(Boolean)) {
        params = params.append('serviceNames', sn);
      }
      params = params.set('orderBy',        this.orderBy        || 'dateCreated');
      params = params.set('orderDirection', this.orderDirection || 'DESC');

      const headers  = new HttpHeaders({ 'Authorization': `Bearer ${this.auth.accessToken}` });
      const response = await firstValueFrom(
        this.http.get<any>(`${API_BASE}/v2/files`, { headers, params })
      );
      this.lastResponse = JSON.stringify(response, null, 2);
      this.parseFilesResponse(response);
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }

  private parseFilesResponse(response: any) {
    if      (Array.isArray(response))           { this.files = response;          this.totalFiles = response.length; }
    else if (Array.isArray(response?.content))  { this.files = response.content;  this.totalFiles = response.totalElements ?? response.content.length; }
    else if (Array.isArray(response?.files))    { this.files = response.files;    this.totalFiles = response.total       ?? response.files.length; }
    else if (Array.isArray(response?.items))    { this.files = response.items;    this.totalFiles = response.totalCount  ?? response.items.length; }
    else if (response && typeof response === 'object') { this.files = [response]; this.totalFiles = 1; }
  }

  get fileKeys(): string[] {
    return this.files.length > 0 ? Object.keys(this.files[0]) : [];
  }

  async downloadFile(file: any) {
    if (!await this.ensureValidToken()) return;
    const fileId = file.id ?? file.fileId ?? file.uuid;
    if (!fileId) { this.lastError = 'No se encontró el ID del fichero en: ' + JSON.stringify(file); return; }

    this.loading   = true;
    this.lastError = '';
    try {
      const headers = new HttpHeaders({ 'Authorization': `Bearer ${this.auth.accessToken}` });
      const blob    = await firstValueFrom(
        this.http.get(`${API_BASE}/files/${fileId}/file`, { headers, responseType: 'blob' })
      );
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = file.name ?? file.filename ?? file.fileName ?? `file-${fileId}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      this.lastError = JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }

  async testCustomEndpoint() {
    if (!await this.ensureValidToken()) return;
    this.loading   = true;
    this.lastError = '';
    try {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${this.auth.accessToken}`,
        'Content-Type':  'application/json',
      });
      let params = new HttpParams();
      if (this.customQueryParams.trim()) {
        for (const pair of this.customQueryParams.split('&')) {
          const [k, v] = pair.split('=');
          if (k && v !== undefined) params = params.set(k.trim(), v.trim());
        }
      }
      const url      = `${API_BASE}${this.customPath}`;
      const response = await firstValueFrom(
        this.http.request(this.customMethod, url, { headers, params, observe: 'response' })
      );
      this.lastResponse = `HTTP ${response.status}\n\n` + JSON.stringify(response.body, null, 2);
    } catch (err: any) {
      this.lastError = `HTTP ${err?.status ?? '?'}\n\n` + JSON.stringify(err?.error ?? err?.message ?? err, null, 2);
    } finally {
      this.loading = false;
    }
  }
}
